import {
  BRAND_FOOTER_KEY,
  IBrandFooterItems,
  ILinkGroup,
  ILinkItem,
} from '@partment/types'
import { getContentfulClient } from '@repo/ui'
import { EntrySkeletonType } from 'contentful'
import { unstable_cache } from 'next/cache'
import { FC } from 'react'
import classes from './BrandFooter.module.css'

import {
  DisclaimerFooter,
  PersonaCardFooter,
  QuickActionFooter,
  RightsFooter,
} from './components'

export interface BrandFooterEntrySkeleton extends EntrySkeletonType<
  IBrandFooterItems['fields'],
  typeof BRAND_FOOTER_KEY
> {}

interface BrandFooterProps {
  variant: 'co-owner' | 'stays'
}

type FooterLocation = {
  id?: string | number
  cityId?: string | number
  city?: string
  city_name?: string
  title?: string
  region?: string
  country?: string
  image?: string | null
  imagePreview?: string | null
  is_trending_active?: boolean
  trending_order?: string | number
}

const STAYS_PROPERTY_LISTING_PATH = '/stays/property-listing'
const STAYS_ADDRESS_LINE = '3 Ebn ElNabeh street, Zamalek, Cairo, Egypt.'

const getTrendingOrder = (location: FooterLocation) => {
  const parsedOrder = Number(location?.trending_order)

  return Number.isFinite(parsedOrder) ? parsedOrder : Number.MAX_SAFE_INTEGER
}

const normalizeLocationOption = (location: FooterLocation): FooterLocation => ({
  ...location,
  id: location?.cityId ?? location?.id,
  cityId: location?.cityId ?? location?.id,
  city: location?.city ?? location?.city_name ?? '',
  city_name: location?.city_name ?? location?.city ?? '',
  title: location?.title ?? location?.region ?? '',
  country: location?.country ?? '',
  imagePreview: location?.imagePreview ?? location?.image ?? null,
})

const getStaysDestinationUrl = (cityName: string) =>
  `${STAYS_PROPERTY_LISTING_PATH}?location=${encodeURIComponent(cityName)}`

const getCurrentCopyright = (copyright?: string) => {
  const currentYear = new Date().getFullYear()

  if (!copyright) {
    return `\u00A9 ${currentYear} Partment. All rights reserved.`
  }

  return copyright.replace(/\u00A9\s*\d{4}/, `\u00A9 ${currentYear}`)
}

const getFallbackLocationName = (item: ILinkItem) =>
  item.label
    .replace(/\s+properties$/i, '')
    .replace(/\s+apartments$/i, '')
    .trim()

const getStaysFooterDestinationItems = unstable_cache(
  async (): Promise<ILinkItem[]> => {
    const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL

    if (!apiBaseUrl) {
      return []
    }

    try {
      const response = await fetch(
        `${apiBaseUrl}/v2/vacation-rental/trending-destination/city`,
        { next: { revalidate: 60 * 60 } },
      )

      if (!response.ok) {
        return []
      }

      const data = await response.json()
      const locations = Array.isArray(data?.data)
        ? data.data
            .map(normalizeLocationOption)
            .filter(
              (location: FooterLocation) =>
                location.city_name &&
                (location.is_trending_active ?? true) &&
                location.imagePreview,
            )
            .sort((left: FooterLocation, right: FooterLocation) => {
              const orderDifference =
                getTrendingOrder(left) - getTrendingOrder(right)

              if (orderDifference !== 0) {
                return orderDifference
              }

              return (left.city_name || '').localeCompare(right.city_name || '')
            })
        : []

      return locations.map((location: FooterLocation) => ({
        label: location.city_name || '',
        url: getStaysDestinationUrl(location.city_name || ''),
      }))
    } catch {
      return []
    }
  },
  ['stays-footer-destinations'],
  {
    revalidate: 60 * 60,
    tags: ['footer', 'stays-destinations'],
  },
)

const getStaysLinkGroups = (
  linkGroups: ILinkGroup[],
  destinationItems: ILinkItem[],
): ILinkGroup[] =>
  linkGroups.map((section) => {
    const sectionTitle = section.title?.toLowerCase()

    if (sectionTitle === 'destinations') {
      return {
        ...section,
        items: destinationItems.length
          ? destinationItems
          : section.items.map((item) => {
              const locationName = getFallbackLocationName(item)

              return {
                ...item,
                label: locationName,
                url: getStaysDestinationUrl(locationName),
              }
            }),
      }
    }

    return {
      ...section,
      items: section.items.map((item) => {
        const shouldLinkToPropertyListing =
          item.label?.toLowerCase().includes('search propert') ||
          item.url?.includes('/property-listing')

        return shouldLinkToPropertyListing
          ? { ...item, url: STAYS_PROPERTY_LISTING_PATH }
          : item
      }),
    }
  })

const getStaysAddress = (address: string) => {
  const [, ...contactLines] = address.split('\n')

  return [STAYS_ADDRESS_LINE, ...contactLines].filter(Boolean).join('\n')
}

const getStaysFooterFields = async (
  fields: IBrandFooterItems['fields'],
): Promise<IBrandFooterItems['fields']> => {
  const destinationItems = await getStaysFooterDestinationItems()

  return {
    ...fields,
    linkGroups: getStaysLinkGroups(fields.linkGroups, destinationItems),
    address: getStaysAddress(fields.address),
    legalText: {
      ...fields.legalText,
      copyright: getCurrentCopyright(fields.legalText?.copyright),
    },
  }
}

const getBrandFooter = unstable_cache(
  async () => {
    return getContentfulClient().getEntries<BrandFooterEntrySkeleton>({
      content_type: BRAND_FOOTER_KEY,
    })
  },
  [BRAND_FOOTER_KEY], // cache key
  {
    revalidate: 60 * 60, // 1 hour
    tags: ['footer'],
  },
)

export const BrandFooter: FC<BrandFooterProps> = async ({ variant }) => {
  const data = await getBrandFooter()

  const footerData = data.items || []

  // Match the variant with Contentful entry
  const matchedFooter = footerData.find(
    (item) =>
      item.fields.variant &&
      String(item.fields.variant).toLowerCase() === variant,
  )

  if (!matchedFooter) {
    console.warn(`No footer found for variant: ${variant}`)
    return null
  }

  const footerFields =
    variant === 'stays'
      ? await getStaysFooterFields(matchedFooter.fields)
      : matchedFooter.fields

  const { cards, linkGroups, address, legalText, socialLinks, disclaimer } =
    footerFields

  return (
    <div className={classes.footerWrapper}>
      <div className={classes.footerContainer}>
        <PersonaCardFooter cards={cards} />
        <QuickActionFooter links={linkGroups} address={address} />
        <RightsFooter rights={legalText} socialLinks={socialLinks} />
        <DisclaimerFooter disclaimer={disclaimer} />
      </div>
    </div>
  )
}
